import { Tag } from "antd";
import trans, {
	formatDuration,
	formatTime,
	splitDateTime,
} from "../../../utils";
import AntdTable from "../../antd/Table/Table";
import Actions from "../../antd/Table/Actions";
import { useContext, useEffect, useState } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useGetAllTasksQuery } from "../../../Redux/service/Tasks/Tasks";
import { useSelector } from "react-redux";
import usePermission from "../../../hooks/usePermission";
import { ALL_TASK_LIST_COLUMN_KEYS } from "./taskListColumns";
import {
	TASK_PRIORITY_OPTIONS,
	TASK_STATUS_OPTIONS,
	TASK_TYPE_OPTIONS,
} from "./taskFilterOptions";
import InfiniteSelectFilterDropdown from "./InfiniteSelectFilterDropdown";
import { useInfinitePoiOptions } from "../../../hooks/useInfinitePoiOptions";
import { useInfiniteTechnicianOptions } from "../../../hooks/useInfiniteTechnicianOptions";

function arraysEqual(a = [], b = []) {
	return a.length === b.length && a.every((v, i) => v === b[i]);
}

const TasksList = ({
	fromDate,
	toDate,
	setRefresh,
	refresh,
	setRefreshing,
	serviceRequestId,
	priority,
	visibleColumnIds = ALL_TASK_LIST_COLUMN_KEYS,
}) => {
	const { edited, setEdited, setDeleteItem, deleteItem } =
		useContext(popupcontext);
	const {
		limit,
		setLimit,
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
	} = useContext(filtrationcontext);
	const [currentPage, setCurrentPage] = useState(1);
	const [sort, setSort] = useState({
		by: "",
		order: "",
	});
	const [filters, setFilters] = useState({
		priority: [],
		status: [],
		type: [],
		poi: [],
		assignedTo: [],
	});
	const [activeFilterKey, setActiveFilterKey] = useState(null);
	const [tasks, setTasks] = useState([]);
	const Livetasks = useSelector((state) => state.webSocket.tasks);
	const {
		data: tasksData,
		isFetching,
		refetch,
	} = useGetAllTasksQuery(
		{
			page: currentPage,
			limit,
			from: serviceRequestId ? undefined : fromDate,
			to: serviceRequestId ? undefined : toDate,
			search: searchQuery,
			sortBy: sort.by,
			sortOrder: sort.order,
			serviceRequestId,
			priority: filters.priority.length ? filters.priority.join(",") : priority,
			status: filters.status.length ? filters.status.join(",") : undefined,
			type: filters.type.length ? filters.type.join(",") : undefined,
			poiId: filters.poi.length ? filters.poi.join(",") : undefined,
			assignedToId: filters.assignedTo.length
				? filters.assignedTo.join(",")
				: undefined,
		},
		{ refetchOnMountOrArgChange: true },
	);

	const { edit, delete: canDelete } = usePermission("FIELD_TASKS");

	const sortFieldMap = {
		assignedTo: "assignedToName",
		createdBy: "createdByName",
		poi: "poiName",
	};

	useEffect(() => {
		setRefreshing?.(isFetching);
	}, [isFetching, setRefreshing]);

	useEffect(() => {
		if (tasksData?.data) {
			setTasks(tasksData?.data || []);
		}
	}, [tasksData]);

	const statusColors = {
		todo: `blue`,
		inProgress: `orange`,
		done: `green`,
		// delayed: `red`,
		unassigned: `default`,
	};

	useEffect(() => {
		async function fetchDataAfterDelete() {
			if (deleteItem.condition === true && deleteItem.value === "task") {
				const res = await refetch();
				const currentData = res?.data?.data || [];

				if (currentData.length === 0 && currentPage > 1) {
					setCurrentPage((prev) => prev - 1);
				}

				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchDataAfterDelete();
	}, [
		deleteItem.condition,
		deleteItem.value,
		currentPage,
		refetch,
		setDeleteItem,
	]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
			if (refresh) {
				await refetch();
				setRefresh(false);
			}
			if (Livetasks) {
				await refetch();
			}
		}
		fetchdata();
	}, [edited, refresh, Livetasks]);

	const columns = [
		{
			title: trans("Task Name"),
			dataIndex: "name",
			sorter: true,
			fixed: "left",
			key: "name",
			filterKey: "name",
			width: 200,
			render: (name) => <span>{name}</span>,
		},
		{
			title: trans("Start Time"),
			dataIndex: "startTime",
			key: "startTime",
			filterKey: "startTime",
			width: 120,
			render: (value) => splitDateTime(value).time,
		},

		{
			title: trans("Duration"),
			dataIndex: "expectedDuration",
			key: "expectedDuration",
			filterKey: "expectedDuration",
			sorter: true,
			width: 120,
			render: (value) => <span>{formatDuration(value)}</span>,
		},
		{
			title: trans("Priority"),
			dataIndex: "priority",
			key: "priority",
			filterKey: "priority",
			width: 120,
			filters: TASK_PRIORITY_OPTIONS.map((opt) => ({
				text: opt.label,
				value: opt.value,
			})),
			filterMultiple: true,
			filteredValue: filters.priority.length ? filters.priority : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("priority");
			},
			render: (value) => (
				<span className="text-capitalize">{trans(value)}</span>
			),
		},
		{
			title: trans("POI Name"),
			dataIndex: "poi",
			key: "poi",
			filterKey: "poi",
			sorter: true,
			width: 200,
			filteredValue: filters.poi.length ? filters.poi : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("poi");
			},
			filterDropdown: (dropdownProps) => (
				<InfiniteSelectFilterDropdown
					{...dropdownProps}
					useOptions={useInfinitePoiOptions}
					searchPlaceholder={trans("Search POI")}
				/>
			),
			render: (poi) => <span>{poi?.name || "Not assigned"}</span>,
		},
		{
			title: trans("status"),
			dataIndex: "status",
			key: "status",
			filterKey: "status",
			sorter: true,
			width: 120,
			filters: TASK_STATUS_OPTIONS.map((opt) => ({
				text: opt.label,
				value: opt.value,
			})),
			filterMultiple: true,
			filteredValue: filters.status.length ? filters.status : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("status");
			},
			render: (value) => (
				<Tag
					color={statusColors[value] || statusColors.unassigned}
					className="text-capitalize"
				>
					{trans(value)}
				</Tag>
			),
		},
		{
			title: trans("Assigned To"),
			dataIndex: "assignedTo",
			sorter: true,
			key: "assignedTo",
			filterKey: "assignedTo",
			width: 200,
			filteredValue: filters.assignedTo.length ? filters.assignedTo : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("assignedTo");
			},
			filterDropdown: (dropdownProps) => (
				<InfiniteSelectFilterDropdown
					{...dropdownProps}
					useOptions={useInfiniteTechnicianOptions}
					searchPlaceholder={trans("Search field staff")}
				/>
			),
			render: (value) => (
				<span className="text-capitalize">
					{value ? value.name || "Unassigned" : "Unassigned"}
				</span>
			),
		},
		{
			title: trans("Type"),
			dataIndex: "type",
			sorter: true,
			key: "type",
			filterKey: "type",
			width: 120,
			filters: TASK_TYPE_OPTIONS.map((opt) => ({
				text: opt.label,
				value: opt.value,
			})),
			filterMultiple: true,
			filteredValue: filters.type.length ? filters.type : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("type");
			},
			render: (value) => (
				<span className="text-capitalize">{trans(value) || "No data"}</span>
			),
		},
		{
			title: trans("Start Date"),
			dataIndex: "startTime",
			sorter: true,
			key: "startTime",
			filterKey: "startDate",
			width: 120,
			render: (value) => splitDateTime(value).date,
		},
		{
			title: trans("Created By"),
			dataIndex: "createdBy",
			sorter: true,
			key: "createdBy",
			filterKey: "createdBy",
			width: 120,
			render: (value) => (
				<span className="text-capitalize">
					{trans(value.name) || "No data"}
				</span>
			),
		},
		{
			title: trans("last update"),
			dataIndex: "updatedAt",
			key: "updatedAt",
			filterKey: "updatedAt",
			width: 120,
			render: (value) => splitDateTime(value).date,
		},
		{
			title: trans("Actions"),
			key: "actions",
			filterKey: "actions",
			render: (_, record) => (
				<Actions
					taskId={record.id}
					name={record.name}
					canEdit={edit}
					canDelete={canDelete}
				/>
			),
		},
	];

	const visibleColumns = columns.filter(
		(col) =>
			col.filterKey === "actions" || visibleColumnIds.includes(col.filterKey),
	);

	const scrollWidth = visibleColumns.reduce(
		(sum, col) => sum + (col.width || 120),
		0,
	);

	const handleTableChange = (pagination, tableFilters, sorter) => {
		const nextSort = sorter.order
			? {
					by: sortFieldMap[sorter.field] || sorter.field,
					order: sorter.order === "ascend" ? "asc" : "desc",
				}
			: { by: "", order: "" };
		const sortChanged =
			nextSort.by !== sort.by || nextSort.order !== sort.order;
		setSort(nextSort);

		const nextFilters = {
			priority:
				activeFilterKey === "priority"
					? tableFilters?.priority || []
					: filters.priority,
			status:
				activeFilterKey === "status"
					? tableFilters?.status || []
					: filters.status,
			type:
				activeFilterKey === "type" ? tableFilters?.type || [] : filters.type,
			poi: activeFilterKey === "poi" ? tableFilters?.poi || [] : filters.poi,
			assignedTo:
				activeFilterKey === "assignedTo"
					? tableFilters?.assignedTo || []
					: filters.assignedTo,
		};
		const filtersChanged =
			!arraysEqual(nextFilters.priority, filters.priority) ||
			!arraysEqual(nextFilters.status, filters.status) ||
			!arraysEqual(nextFilters.type, filters.type) ||
			!arraysEqual(nextFilters.poi, filters.poi) ||
			!arraysEqual(nextFilters.assignedTo, filters.assignedTo);
		setFilters(nextFilters);

		if (filtersChanged || sortChanged) {
			setCurrentPage(1);
		} else if (pagination?.current) {
			setCurrentPage(pagination.current);
		}
	};

	return (
		<div className="bg-white-rounded-top mt-3 table-parent">
			<AntdTable
				columns={visibleColumns}
				dataSource={tasks || []}
				loading={isFetching}
				pageSize={limit}
				totalItems={tasksData?.totalResults ?? 0}
				currentPage={currentPage}
				onPageChange={(page) => setCurrentPage(page)}
				onLimitChange={(newLimit) => {
					setLimit(newLimit);
					setCurrentPage(1);
				}}
				onTableChange={handleTableChange}
				scrollX={scrollWidth}
			/>
		</div>
	);
};

export default TasksList;
